import { useEffect, useLayoutEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { markDerivedStructureSet } from '~/domain/patient/derivedStructureSet';
import { StructureSet } from '~/domain/patient/structureset';
import { StructureSetCopyService } from '~/domain/services/structureSetService/structureSetCopyService';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useStructureSetActivation from '~/hooks/datasetSelection/useStructureSetActivation';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import {
    replanningQaContourSetState,
    replanningRunOutputsState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { useReplanningStructureSets } from './useReplanningStructureSets';

// Shows the run's target-scan Contour+ set on the QA target panel as a session-only derived COPY (the detached set
// stays untouched as the measurement reference), removed on close.
export function useQaContourComparison(
    targetContourSet: StructureSet | undefined
): void {
    const patient = useSelectedPatient();
    const session = useSessionOrNull();
    const { propagatedUid: forRun } = useRecoilValue(replanningRunOutputsState);
    const { propagated } = useReplanningStructureSets();
    const [record, setRecord] = useRecoilState(replanningQaContourSetState);
    const { activateHeld } = useStructureSetActivation();
    const { deleteStructureSet } = useDeleteDataset();

    const added =
        record && record.forRun === forRun
            ? patient?.structureSets.find(
                  (set) => set.dicomUid === record.structureSetUid
              )
            : undefined;

    // Add the Contour+ set to the patient, once per run, so the primary panel can select and show it.
    useEffect(() => {
        if (
            !forRun ||
            !targetContourSet ||
            added ||
            !patient ||
            !session ||
            record?.forRun === forRun
        ) {
            return;
        }
        // A record from a previous run: drop its derived set before adding this run's.
        if (record) {
            const stale = patient.structureSets.find(
                (set) => set.dicomUid === record.structureSetUid
            );
            if (stale) {
                deleteStructureSet(stale);
            }
        }
        const targetImage = targetContourSet.image;
        if (!targetImage) {
            return;
        }
        let copy: StructureSet;
        try {
            copy = new StructureSetCopyService().copySameGeometry({
                sourceStructureSet: targetContourSet,
                targetImage,
                existingStructureSets: patient.structureSets,
                selectedRoiNumbers: new Set(
                    targetContourSet.roiArray.map((roi) => roi.roiNumber)
                ),
            }).structureSet;
        } catch {
            return;
        }
        markDerivedStructureSet(copy);
        session.context.tracker.batch(() => patient.addStructureSet(copy));
        setRecord({ forRun, structureSetUid: copy.dicomUid });
    }, [
        forRun,
        targetContourSet,
        added,
        patient,
        session,
        record,
        deleteStructureSet,
        setRecord,
    ]);

    // Select it on the primary once it is in the patient, once per set so a later choice is not undone.
    const selectedFor = useRef<string | undefined>();
    useEffect(() => {
        if (added && selectedFor.current !== added.dicomUid) {
            selectedFor.current = added.dicomUid;
            void activateHeld(added);
        }
    }, [added, activateHeld]);

    // Held: removal runs from an unmount, after these have moved on.
    const held = useRef({
        patient,
        deleteStructureSet,
        record,
        propagated,
        activateHeld,
    });
    useLayoutEffect(() => {
        held.current = {
            patient,
            deleteStructureSet,
            record,
            propagated,
            activateHeld,
        };
    });

    useEffect(
        () => () => {
            const current = held.current;
            const set = current.record
                ? current.patient?.structureSets.find(
                      (candidate) =>
                          candidate.dicomUid === current.record?.structureSetUid
                  )
                : undefined;
            if (set) {
                current.deleteStructureSet(set);
            }
            // Restore the run's merged set so the next step does not open on an empty primary.
            const propagatedSet = current.propagated?.dicomUid
                ? current.patient?.structureSets.find(
                      (candidate) =>
                          candidate.dicomUid === current.propagated?.dicomUid
                  )
                : undefined;
            if (propagatedSet) {
                void current.activateHeld(propagatedSet);
            }
            setRecord(undefined);
        },
        [setRecord]
    );
}
